import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { marked } from "marked";
import { CheckCircle2, RotateCcw, ChevronLeft, ChevronDown, ChevronRight, Crown, BookOpen, ScrollText, Coins, Sparkles, Trophy, Swords } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { btnPrimary, btnSecondary, btnGhost, card, label, LEVEL_COLORS } from "@/lib/styles";
import Narrator from "@/components/Narrator";

function Flashcard({ q, a, idx }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="flip-card cursor-pointer min-h-[180px]" onClick={() => setFlipped((f) => !f)} data-testid={`flashcard-${idx}`}>
      <div className={`flip-inner min-h-[180px] ${flipped ? "" : ""}`} style={{ transform: flipped ? "rotateY(180deg)" : "none" }}>
        <div className="flip-face border border-zinc-800 bg-zinc-950 p-6 min-h-[180px] flex flex-col justify-between scanlines relative">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-600">card {idx + 1} · question</span>
            <p className="mt-3 text-base text-zinc-100 leading-relaxed font-medium">{q}</p>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><RotateCcw size={11} /> click to reveal</span>
        </div>
        <div className="flip-face flip-back border border-emerald-500/40 bg-emerald-950/15 p-6 min-h-[180px] flex flex-col justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-emerald-400">answer</span>
            <p className="mt-3 text-sm text-zinc-200 leading-relaxed">{a}</p>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><RotateCcw size={11} /> click to flip back</span>
        </div>
      </div>
    </div>
  );
}

export function LessonReader({ chapters }) {
  const [open, setOpen] = useState(chapters.length === 1 ? chapters[0].slug : null);
  if (!chapters.length) return null;
  return (
    <div className="mt-8" data-testid="lesson-reader">
      <span className={label}>Altaris's lessons</span>
      <p className="mt-2 text-sm text-zinc-400 italic">"Sit, apprentice. Read closely: my exam has little patience for those who skim."</p>
      <div className="mt-3 space-y-2">
        {chapters.map((ch, i) => {
          const isOpen = open === ch.slug;
          return (
            <div key={ch.slug} className="border border-zinc-800 bg-zinc-950">
              <button
                data-testid={`lesson-chapter-${ch.slug}`}
                onClick={() => setOpen(isOpen ? null : ch.slug)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-900/60 transition-colors"
              >
                {isOpen ? <ChevronDown size={15} className="text-gold shrink-0" /> : <ChevronRight size={15} className="text-zinc-600 shrink-0" />}
                <ScrollText size={15} className="text-gold shrink-0" />
                <span className="text-sm font-medium text-zinc-100">{ch.title}</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-600">chapter {i + 1}</span>
              </button>
              {isOpen && (
                <div className="lesson-prose px-5 pb-6 border-t border-zinc-800">
                  <Narrator src={`/media/lessons/${ch.slug}.mp3`} />
                  <div
                    // markdown is our own authored lesson content served by the backend
                    dangerouslySetInnerHTML={{ __html: marked.parse(ch.markdown) }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuizTrial({ topicId, onReward }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const begin = async () => {
    setLoading(true); setResult(null); setAnswers({});
    try {
      const { data } = await api.get(`/topics/${topicId}/quiz`);
      setQuiz(data);
    } catch { toast.error("Could not load the trial. Please retry."); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) { toast.error("Answer every question first"); return; }
    setLoading(true);
    try {
      const ans = quiz.questions.map((_, i) => (answers[i] ?? -1));
      const { data } = await api.post(`/topics/${topicId}/quiz/submit`, { answers: ans });
      setResult(data);
      if (data.gold_earned > 0) toast.success(`Trial cleared! +${data.gold_earned} gold · +${data.xp_earned} XP`);
      else if (data.already_completed) toast.message("Trial already claimed. No new loot, but good practice!");
      onReward?.();
    } catch { toast.error("Could not submit. Please retry."); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-10" data-testid="quiz-trial">
      <span className={label}>Trial of Knowledge</span>
      <div className={`${card} p-6 mt-3`}>
        {!quiz && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ScrollText className="text-gold shrink-0" />
              <div>
                <h3 className="font-serif font-bold text-lg text-zinc-100">Prove your understanding</h3>
                <p className="text-sm text-zinc-400">Answer 3 questions to earn <span className="text-gold font-semibold">gold</span> and <span className="text-gem-light font-semibold">XP</span>. First clear pays out the loot.</p>
              </div>
            </div>
            <button data-testid="quiz-begin" onClick={begin} disabled={loading} className={btnPrimary}>{loading ? "summoning..." : "Begin trial"}</button>
          </div>
        )}

        {quiz && (
          <div className="space-y-6">
            {quiz.questions.map((q, qi) => (
              <div key={qi} data-testid={`quiz-q-${qi}`}>
                <p className="font-medium text-zinc-100 mb-2"><span className="text-gold font-serif font-bold mr-1">{qi + 1}.</span>{q.q}</p>
                <div className="grid grid-cols-1 gap-2">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    let cls = "border-zinc-800 hover:border-zinc-600 text-zinc-300";
                    if (result) {
                      const r = result.results[qi];
                      if (oi === r.correct_index) cls = "border-emerald-500/60 bg-emerald-500/10 text-emerald-200";
                      else if (oi === r.your_index) cls = "border-rose-500/60 bg-rose-500/10 text-rose-200";
                      else cls = "border-zinc-800 text-zinc-500";
                    } else if (chosen) cls = "border-gold/70 bg-gold/5 text-zinc-100";
                    return (
                      <button
                        key={oi}
                        data-testid={`quiz-opt-${qi}-${oi}`}
                        disabled={!!result}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        className={`text-left text-sm border rounded-md px-4 py-2.5 transition-all flex items-center gap-2 ${cls}`}
                      >
                        <span className="text-xs font-mono opacity-60">{String.fromCharCode(65 + oi)}</span> {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {!result ? (
              <div className="flex justify-end gap-3">
                <button data-testid="quiz-submit" onClick={submit} disabled={loading} className={btnPrimary}><Sparkles size={16} /> {loading ? "judging..." : "Submit answers"}</button>
              </div>
            ) : (
              <div className="border border-gold/30 bg-gold/5 rounded-lg p-5 flex flex-wrap items-center justify-between gap-4" data-testid="quiz-result">
                <div className="flex items-center gap-3">
                  <Trophy className="text-gold" />
                  <div>
                    <p className="font-serif font-bold text-xl text-zinc-100">{result.correct} / {result.total} correct</p>
                    {result.gold_earned > 0
                      ? <p className="text-sm text-gold flex items-center gap-1"><Coins size={14} /> +{result.gold_earned} gold · +{result.xp_earned} XP</p>
                      : <p className="text-sm text-zinc-500">Already claimed. No new loot this time.</p>}
                  </div>
                </div>
                <button data-testid="quiz-retake" onClick={begin} className={btnSecondary}><RotateCcw size={14} /> Retake</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Topic() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [topic, setTopic] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    api.get(`/topics/${topicId}`).then((r) => setTopic(r.data)).catch(() => toast.error("Topic not found"));
    api.get(`/topics/${topicId}/lessons`).then((r) => setChapters(r.data.chapters)).catch(() => setChapters([]));
  }, [topicId]);

  const markComplete = async () => {
    await api.post(`/topics/${topicId}/complete`);
    setTopic((t) => ({ ...t, completed: true }));
    toast.success("Marked as studied");
  };

  const startInterview = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/interview/start", { topic_id: topicId });
      navigate(`/interview/${data.session_id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Free trial used up. Upgrade to Pro");
        navigate("/pricing");
      } else {
        toast.error(e.response?.data?.detail || "Could not start interview");
      }
    } finally { setStarting(false); refreshUser(); }
  };

  if (!topic) return <div className="min-h-[60vh] flex items-center justify-center text-emerald-400 font-mono"><span className="cursor-blink">loading</span></div>;

  const isPro = user?.plan === "pro";

  const header = (
    <>
      <Link to="/curriculum" className={btnGhost}><ChevronLeft size={16} /> Curriculum</Link>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-emerald-400">Tier {topic.tier} · {topic.tier_meta?.title}</span>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${LEVEL_COLORS[topic.level]}`}>{topic.level}</span>
        {topic.completed && <span className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> studied</span>}
      </div>
      <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight leading-tight">{topic.title}</h1>
    </>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {header}

        {!started ? (
          <>
            {/* briefing */}
            <div className={`${card} p-6 mt-8`}>
              <span className={label}>What you'll learn</span>
              <p className="mt-3 text-zinc-300 leading-relaxed">{topic.scope_summary}</p>

              <span className={`${label} mt-8 block`}>Key ideas to watch for</span>
              <div className="mt-3 space-y-2">
                {topic.concepts.map((c, i) => (
                  <div key={i} className="flex gap-3 border border-zinc-800 bg-zinc-950 p-4">
                    <span className="text-emerald-400 font-mono font-bold text-sm shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-zinc-300 leading-relaxed">{c}</p>
                  </div>
                ))}
              </div>

              {topic.takeaways?.length > 0 && (
                <>
                  <span className={`${label} mt-8 block`}>You'll walk away able to…</span>
                  <div className="mt-3 space-y-2">
                    {topic.takeaways.map((t, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-zinc-300 leading-relaxed">{t}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                data-testid="start-lesson-btn"
                onClick={() => { setStarted(true); window.scrollTo({ top: 0 }); }}
                className={btnPrimary}
              >
                <BookOpen size={16} /> Start lesson
              </button>
            </div>
          </>
        ) : (
          <>
            <button data-testid="back-to-overview" onClick={() => setStarted(false)} className={`${btnGhost} mt-8`}>‹ Overview</button>

            <LessonReader chapters={chapters} />

            {/* flashcards */}
            <div className="mt-8">
              <span className={label}>self-check flashcards</span>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {topic.flashcards.map((f, i) => <Flashcard key={i} q={f.q} a={f.a} idx={i} />)}
              </div>
            </div>

            <QuizTrial topicId={topicId} onReward={refreshUser} />

            {/* actions */}
            <div className="mt-10 border border-emerald-500/30 bg-emerald-950/10 p-6 scanlines relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-sans font-bold text-lg tracking-tight flex items-center gap-2"><Sparkles size={18} className="text-emerald-400" /> Face Altaris</h3>
                <p className="text-sm text-zinc-400 mt-1">Altaris awaits with a FAANG-grade oral exam on this topic. {!isPro && "Uses one of your free trial interviews."}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                {!topic.completed && <button data-testid="mark-complete-btn" onClick={markComplete} className={btnSecondary}><BookOpen size={16} /> Mark studied</button>}
                <button data-testid="start-interview-btn" onClick={startInterview} disabled={starting} className={btnPrimary}>
                  {!isPro && <Crown size={16} />} {starting ? "starting..." : "Start mock interview"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
